/* eslint-disable react/jsx-fragments */
import React, { Component } from "react";
import PropTypes from "prop-types";
import cloneDeep from "lodash-es/cloneDeep";
import isEqual from "lodash-es/isEqual";

import { isFunction, gatherClasses } from "../../lib/utils";
import {
  combineFilters,
  popoverDataParser,
  ToolTipWrapper
} from "../../lib/filterButtonUtils";
import Form from "../form/form";
import Input from "../input/input";
import FormGroup from "../formGroup/formGroup";
import Popover from "../popover/popover";
import Button from "../button/button";
import "./filterButton.scss";

class FilterButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      togglePopOver: false,
      appliedFilters: (props.filters && combineFilters(props)) || []
    };
  }

  onDocumentMouseDown = ({ target }) => {
    const { onClose } = this.props;

    const nodeList = document.getElementsByClassName(
      "filter-button-modal-popover"
    )[0];
    if (
      target.getClientRects().length &&
      nodeList &&
      !nodeList.contains(target)
    ) {
      this.setState({ togglePopOver: false }, () => {
        if (isFunction(onClose)) {
          onClose();
        }
      });
    }
  };

  onDocumentKeyUp = e => {
    const { onClose } = this.props;

    if (e.key === "Escape") {
      this.setState({ togglePopOver: false }, () => {
        if (isFunction(onClose)) {
          onClose();
        }
      });
    }
  };

  addCloseEvents = () => {
    document.addEventListener("mousedown", this.onDocumentMouseDown);
    document.addEventListener("keyup", this.onDocumentKeyUp);
  };

  removeCloseEvents = () => {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
    document.removeEventListener("keyup", this.onDocumentKeyUp);
  };

  handleTogglePopOver = () => {
    const { onOpen, onClose } = this.props;

    this.setState(oldState => {
      const newToggleState = !oldState.togglePopOver;

      if (newToggleState === true) {
        this.addCloseEvents();
        if (isFunction(onOpen)) {
          onOpen();
        }
      } else {
        this.removeCloseEvents();
        if (isFunction(onClose)) {
          onClose();
        }
      }

      return { togglePopOver: newToggleState };
    });
  };

  handleSubmit = newAppliedFilters => {
    const { onSubmit, onClose } = this.props;
    const { appliedFilters: stateAppliedFilters } = this.state;
    const appliedFilters = cloneDeep(stateAppliedFilters);

    appliedFilters.forEach(filter => {
      filter.value = newAppliedFilters[filter.fieldName];
    });

    this.setState({ appliedFilters, togglePopOver: false }, () => {
      onSubmit(newAppliedFilters);
      if (isFunction(onClose)) {
        onClose();
      }
    });
  };

  onButtonMouseDown = () => {
    const { togglePopOver } = this.state;

    if (togglePopOver) {
      this.removeCloseEvents();
    }
  };

  shouldComponentUpdate(nextProps, nextState) {
    const previousAppliedFilters = combineFilters(this.props, this.state);
    const appliedFilters = combineFilters(nextProps, nextState);
    if (!isEqual(previousAppliedFilters, appliedFilters)) {
      this.setState({ appliedFilters });
    }
    return true;
  }

  componentWillUnmount() {
    this.removeCloseEvents();
  }

  render() {
    const { togglePopOver, appliedFilters } = this.state;
    const {
      popoverPlacement,
      filterTitle,
      popoverSize,
      renderFilters,
      filters,
      validationSchema,
      tooltipGroups,
      onReset,
      shouldDisableApply,
      popoverClassName,
      className,
      dataTestId,
      ariaLabel,
      hideFooter,
      buttonRef
    } = this.props;

    const initialValues = popoverDataParser(appliedFilters);

    return (
      <Popover
        title={filterTitle}
        placement={popoverPlacement}
        className={gatherClasses(
          null,
          "filter-button-modal-popover",
          popoverClassName
        )}
        body={() => {
          return (
            <Form
              withValidation
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={value => this.handleSubmit(value)}
              renderChildren={formikProps => {
                const isFormInvalid =
                  Object.keys(formikProps.errors).length > 0;
                return (
                  <React.Fragment>
                    {renderFilters(formikProps)}
                    {!hideFooter && (
                      <FormGroup last footer>
                        <Button
                          label="Reset"
                          className="oui-mr-3"
                          onClick={event => {
                            formikProps.setValues(popoverDataParser(filters));
                            formikProps.validateForm();
                            if (isFunction(onReset)) {
                              onReset(event);
                            }
                          }}
                        />
                        <Input
                          type="submit"
                          label="Apply"
                          disabled={
                            (isFunction(shouldDisableApply) &&
                              shouldDisableApply(
                                formikProps,
                                initialValues,
                                appliedFilters
                              ) === true) ||
                            isFormInvalid
                          }
                        />
                      </FormGroup>
                    )}
                  </React.Fragment>
                );
              }}
            />
          );
        }}
        size={popoverSize}
        isOpen={togglePopOver}
        toggle={this.handleTogglePopOver}
      >
        <ToolTipWrapper
          appliedFilters={appliedFilters}
          onButtonMouseDown={this.onButtonMouseDown}
          togglePopOver={togglePopOver}
          tooltipGroups={tooltipGroups}
          className={className}
          ariaLabel={ariaLabel}
          dataTestId={dataTestId}
          buttonRef={buttonRef}
        />
      </Popover>
    );
  }
}

FilterButton.defaultProps = {
  appliedFilters: [],
  filterTitle: "",
  popoverPlacement: "bottom-end",
  popoverSize: "sm",
  // eslint-disable-next-line react/default-props-match-prop-types
  filters: [],
  onSubmit: () => {},
  onReset: null,
  shouldDisableApply: () => false,
  className: "",
  popoverClassName: "",
  ariaLabel: "filter",
  dataTestId: "orion-filter-button",
  hideFooter: false
};

FilterButton.propTypes = {
  popoverPlacement: PropTypes.string,
  filterTitle: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
            label: PropTypes.string.isRequired
          })
        ),
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          label: PropTypes.string.isRequired
        })
      ]),
      fieldName: PropTypes.string.isRequired,
      valueRenderer: PropTypes.func
    })
  ).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  appliedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
            label: PropTypes.string.isRequired
          })
        ),
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          label: PropTypes.string.isRequired
        })
      ]),
      fieldName: PropTypes.string.isRequired
    })
  ),
  renderFilters: PropTypes.func.isRequired,
  popoverSize: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  validationSchema: PropTypes.func,
  onSubmit: PropTypes.func,
  tooltipGroups: PropTypes.arrayOf(
    PropTypes.shape({
      groupName: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(PropTypes.string).isRequired,
      groupRenderer: PropTypes.func.isRequired
    })
  ),
  onReset: PropTypes.func,
  shouldDisableApply: PropTypes.func,
  className: PropTypes.string,
  popoverClassName: PropTypes.string,
  ariaLabel: PropTypes.string,
  dataTestId: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  hideFooter: PropTypes.bool,
  buttonRef: PropTypes.object
};

FilterButton.displayName = "OrionFilterButton";

export default FilterButton;
